export const languages: Record<string, string> = {
	af: 'Afrikaans',
	sq: 'Albanian',
	am: 'Amharic',
	ar: 'Arabic',
	hy: 'Armenian',
	az: 'Azerbaijani',
	eu: 'Basque',
	be: 'Belarusian',
	bn: 'Bengali',
	bs: 'Bosnian',
	bg: 'Bulgarian',
	ca: 'Catalan',
	ceb: 'Cebuano',
	ny: 'Chichewa',
	'zh-CN': 'Chinese (Simplified)',
	'zh-TW': 'Chinese (Traditional)',
	co: 'Corsican',
	hr: 'Croatian',
	cs: 'Czech',
	da: 'Danish',
	nl: 'Dutch',
	en: 'English',
	eo: 'Esperanto',
	et: 'Estonian',
	tl: 'Filipino',
	fi: 'Finnish',
	fr: 'French',
	fy: 'Frisian',
	gl: 'Galician',
	ka: 'Georgian',
	de: 'German',
	el: 'Greek',
	gu: 'Gujarati',
	ht: 'Haitian Creole',
	ha: 'Hausa',
	haw: 'Hawaiian',
	iw: 'Hebrew',
	hi: 'Hindi',
	hmn: 'Hmong',
	hu: 'Hungarian',
	is: 'Icelandic',
	ig: 'Igbo',
	id: 'Indonesian',
	ga: 'Irish',
	it: 'Italian',
	ja: 'Japanese',
	jw: 'Javanese',
	kn: 'Kannada',
	kk: 'Kazakh',
	km: 'Khmer',
	rw: 'Kinyarwanda',
	ko: 'Korean',
	ku: 'Kurdish',
	ky: 'Kyrgyz',
	lo: 'Lao',
	la: 'Latin',
	lv: 'Latvian',
	lt: 'Lithuanian',
	lb: 'Luxembourgish',
	mk: 'Macedonian',
	mg: 'Malagasy',
	ms: 'Malay',
	ml: 'Malayalam',
	mt: 'Maltese',
	mi: 'Maori',
	mr: 'Marathi',
	mn: 'Mongolian',
	my: 'Myanmar (Burmese)',
	ne: 'Nepali',
	no: 'Norwegian',
	or: 'Odia (Oriya)',
	ps: 'Pashto',
	fa: 'Persian',
	pl: 'Polish',
	pt: 'Portuguese',
	pa: 'Punjabi',
	ro: 'Romanian',
	ru: 'Russian',
	sm: 'Samoan',
	gd: 'Scots Gaelic',
	sr: 'Serbian',
	st: 'Sesotho',
	sn: 'Shona',
	sd: 'Sindhi',
	si: 'Sinhala',
	sk: 'Slovak',
	sl: 'Slovenian',
	so: 'Somali',
	es: 'Spanish',
	su: 'Sundanese',
	sw: 'Swahili',
	sv: 'Swedish',
	tg: 'Tajik',
	ta: 'Tamil',
	tt: 'Tatar',
	te: 'Telugu',
	th: 'Thai',
	tr: 'Turkish',
	tk: 'Turkmen',
	uk: 'Ukrainian',
	ur: 'Urdu',
	ug: 'Uyghur',
	uz: 'Uzbek',
	vi: 'Vietnamese',
	cy: 'Welsh',
	xh: 'Xhosa',
	yi: 'Yiddish',
	yo: 'Yoruba',
	zu: 'Zulu',
};

export const applyTranslation = (targetLang: string): Promise<boolean> => {
	return new Promise((resolve, reject) => {
		try {
			// First, clean up any existing translation elements
			cleanupTranslationElements();

			// If English is selected, reset cookies and reload
			if (targetLang === 'en') {
				// Reset cookies
				resetTranslationCookies();
				// The page reload is now handled in resetTranslationCookies
				resolve(true);
				return;
			}

			// For non-English languages, set cookies
			setTranslationCookies(targetLang);

			// Try the direct element method first (works in-page)
			const success = useElementTranslationMethod(targetLang);

			if (success) {
				// If successful, resolve after a short delay
				setTimeout(() => {
					resolve(true);
				}, 1000);
				return;
			}

			// If element method didn't work, try direct URL method
			const directTranslateUrl = `https://translate.google.com/translate?sl=en&tl=${targetLang}&u=${encodeURIComponent(window.location.href)}`;
			const translateWindow = window.open(directTranslateUrl, '_blank');

			// If popup blocked, try iframe method
			if (!translateWindow) {
				useIframeTranslationMethod(targetLang);
			}

			// Resolve after a delay
			setTimeout(() => {
				resolve(true);
			}, 2000);
		} catch (error) {
			console.error('Error applying translation:', error);
			reject(error);
		}
	});
};

export const cleanupTranslationElements = (): void => {
	const elementsToRemove = document.querySelectorAll(
		'.skiptranslate, .goog-te-banner-frame, iframe.goog-te-menu-frame, #google_translate_element, ' +
			'.goog-te-gadget, .goog-te-menu-value, .goog-tooltip, .goog-tooltip:hover, ' +
			'.VIpgJd-ZVi9od-l4eHX-hSRGPd, .VIpgJd-ZVi9od-ORHb-OEVmcd, .VIpgJd-ZVi9od-SmfZ-OEVmcd-tJHJj, ' +
			'.VIpgJd-ZVi9od-aZ2wEe-wOHMyf, .VIpgJd-ZVi9od-aZ2wEe-OiiCO, .VIpgJd-ZVi9od-aZ2wEe, ' +
			'.VIpgJd-yAWNEb-L7lbkb, .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Hf',
	);

	elementsToRemove.forEach((el) => {
		if (el.parentNode) {
			el.parentNode.removeChild(el);
		}
	});

	document.body.style.top = '0px';
	document.body.style.position = 'static';

	const style = document.createElement('style');
	style.textContent = `
    .goog-te-banner-frame, .skiptranslate, .goog-te-gadget, 
    .goog-te-menu-frame, .VIpgJd-ZVi9od-ORHb, .VIpgJd-ZVi9od-ORHb-OEVmcd, 
    .VIpgJd-ZVi9od-l4eHX-hSRGPd, .VIpgJd-ZVi9od-aZ2wEe {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }
    body { top: 0 !important; position: static !important; }
  `;
	document.head.appendChild(style);
};

export const resetTranslationCookies = (): void => {
	const hostname = window.location.hostname;
	const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
	const domains = [
		'', // No domain (for path=/)
		hostname, // Full hostname
	];

	if (!isLocalhost && hostname.indexOf('.') !== -1) {
		domains.push('.' + hostname);

		const rootDomain = hostname.split('.').slice(-2).join('.');
		domains.push(rootDomain);
		domains.push('.' + rootDomain);
	}

	const paths = ['/', '/en/', window.location.pathname];

	// Clear cookies for all combinations of domains and paths
	domains.forEach((domain) => {
		paths.forEach((path) => {
			// Basic cookie clearing
			document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? `; domain=${domain}` : ''}`;

			// Try with secure and SameSite attributes
			document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? `; domain=${domain}` : ''}; secure; SameSite=None`;
			document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? `; domain=${domain}` : ''}; SameSite=Lax`;

			// Also clear Google's other cookies that might affect translation
			document.cookie = `googtrans_old=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? `; domain=${domain}` : ''}`;
			document.cookie = `GOOGTRANS=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? `; domain=${domain}` : ''}`;
		});
	});

	try {
		document.documentElement.lang = 'en';
		document.documentElement.removeAttribute('translate');
	} catch (e) {
		console.error('Error resetting HTML attributes:', e);
	}

	// Simply reload the page without adding any query parameters
	window.location.reload();
};

export const setTranslationCookies = (targetLang: string): void => {
	const now = new Date();
	const time = now.getTime();
	const expireTime = time + 1000 * 3600 * 24 * 365; // One year from now
	now.setTime(expireTime);

	const cookieValue = `/en/${targetLang}`;
	document.cookie = `googtrans=${cookieValue}; expires=${now.toUTCString()}; path=/;`;
	document.cookie = `googtrans=${cookieValue}; expires=${now.toUTCString()}; path=/; domain=${window.location.hostname};`;
	document.cookie = `googtrans=${cookieValue}; expires=${now.toUTCString()}; path=/; domain=.${window.location.hostname};`;
};

export const useIframeTranslationMethod = (targetLang: string): void => {
	const iframe = document.createElement('iframe');
	iframe.style.display = 'none';
	iframe.src = `https://translate.google.com/translate_p?hl=en&sl=en&tl=${targetLang}&u=${encodeURIComponent(window.location.href)}`;
	document.body.appendChild(iframe);

	setTimeout(() => {
		if (document.body.contains(iframe)) {
			document.body.removeChild(iframe);
		}
	}, 5000);
};

export const useElementTranslationMethod = (targetLang: string): boolean => {
	try {
		const gtElement = document.createElement('div');
		gtElement.id = 'google_translate_element';
		gtElement.style.display = 'none';
		document.body.appendChild(gtElement);

		window.googleTranslateElementInit = function () {
			if (window.google && window.google.translate) {
				new window.google.translate.TranslateElement(
					{
						pageLanguage: 'en',
						includedLanguages: Object.keys(languages).join(','),
						autoDisplay: false,
						gaTrack: false,
						gaId: '',
						layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
					},
					'google_translate_element',
				);

				const style = document.createElement('style');
				style.textContent = `
          .goog-te-banner-frame, .skiptranslate, .goog-te-gadget, 
          .goog-te-menu-frame, .VIpgJd-ZVi9od-ORHb, .VIpgJd-ZVi9od-ORHb-OEVmcd, 
          .VIpgJd-ZVi9od-l4eHX-hSRGPd, .VIpgJd-ZVi9od-aZ2wEe {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
          }
          body { top: 0 !important; position: static !important; }
        `;
				document.head.appendChild(style);

				setTimeout(() => {
					const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
					if (selectElement) {
						selectElement.value = targetLang;
						selectElement.dispatchEvent(new Event('change', { bubbles: true }));
						setTimeout(cleanupTranslationElements, 500);
						return true;
					}
				}, 500);
			}
		};

		const script = document.createElement('script');
		script.src =
			'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
		script.async = true;
		document.body.appendChild(script);

		setTimeout(() => {
			if (document.body.contains(script)) {
				document.body.removeChild(script);
			}
			cleanupTranslationElements();
		}, 5000);

		return true;
	} catch (error) {
		console.error('Error using element translation method:', error);
		return false;
	}
};

export const getLanguageName = (code: string): string => {
	return languages[code] || code;
};
